
import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { trpc } from '@/utils/trpc';
import { tokens } from '@/lib/palette';

export interface WhitelabelSettings {
  portalName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  footerText: string;
  customCss: string;
}

interface WhitelabelContextType {
  settings: WhitelabelSettings;
  isLoading: boolean;
}

const defaultSettings: WhitelabelSettings = {
  portalName: 'Sekura SCS',
  logoUrl: '/sekura-logo.svg',
  faviconUrl: null,
  primaryColor: tokens.primary,
  secondaryColor: tokens.chart2,
  footerText: 'Safe. Secure. Sustainable.',
  customCss: '',
};

const legacyDefaults = {
  portalName: 'Smart City TVMS',
  primaryColor: '#3182CE',
  secondaryColor: '#718096',
  footerText: 'Smart City TVMS - Traffic & Vehicle Management System',
};

const WhitelabelContext = createContext<WhitelabelContextType>({
  settings: defaultSettings,
  isLoading: true,
});

export function useWhitelabel() {
  return useContext(WhitelabelContext);
}

export function WhitelabelProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = trpc.whitelabel.getSettings.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const settings: WhitelabelSettings = {
    portalName: !data?.portalName || data.portalName === legacyDefaults.portalName
      ? defaultSettings.portalName
      : data.portalName,
    logoUrl: data?.logoUrl || defaultSettings.logoUrl,
    faviconUrl: data?.faviconUrl || defaultSettings.faviconUrl,
    primaryColor: !data?.primaryColor || data.primaryColor === legacyDefaults.primaryColor
      ? defaultSettings.primaryColor
      : data.primaryColor,
    secondaryColor: !data?.secondaryColor || data.secondaryColor === legacyDefaults.secondaryColor
      ? defaultSettings.secondaryColor
      : data.secondaryColor,
    footerText: !data?.footerText || data.footerText === legacyDefaults.footerText
      ? defaultSettings.footerText
      : data.footerText,
    customCss: data?.customCss || defaultSettings.customCss,
  };

  // Apply CSS variables for brand colors
  useEffect(() => {
    if (isLoading) return;
    const root = document.documentElement;
    root.style.setProperty('--whitelabel-primary', settings.primaryColor);
    root.style.setProperty('--whitelabel-secondary', settings.secondaryColor);
  }, [isLoading, settings.primaryColor, settings.secondaryColor]);

  // Apply document title
  useEffect(() => {
    if (isLoading) return;
    document.title = settings.portalName;
  }, [isLoading, settings.portalName]);

  // Apply favicon
  useEffect(() => {
    if (isLoading || !settings.faviconUrl) return;
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = settings.faviconUrl;
  }, [isLoading, settings.faviconUrl]);

  // Inject custom CSS
  useEffect(() => {
    if (isLoading) return;
    const styleId = 'whitelabel-custom-css';
    let style = document.getElementById(styleId) as HTMLStyleElement | null;

    if (settings.customCss) {
      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }
      style.textContent = settings.customCss;
    } else if (style) {
      style.remove();
    }
  }, [isLoading, settings.customCss]);

  return (
    <WhitelabelContext.Provider value={{ settings, isLoading }}>
      {children}
    </WhitelabelContext.Provider>
  );
}
